import { Button, DatePicker, Form, Input, Modal, Select, Space } from 'antd';
import React from 'react';
import { useCreateMenu, useFoodQuery } from '../api';
import { useNotifixationStore } from '../stores/notification';
import { PlusOutlined } from '@ant-design/icons';

type AddFoodToMenuProps = {
  open: boolean;
  onClose: () => void;
  onFinish: () => void;
  onAddFood: () => void;
};

const AddFoodToMenu = ({
  open,
  onClose,
  onFinish,
  onAddFood,
}: AddFoodToMenuProps) => {
  const [form] = Form.useForm();
  const { data: foods, refetch } = useFoodQuery();
  const [food, setFood] = React.useState('');
  const [selectedDates, setSelectedDates] = React.useState<string[]>([]);
  const notification = useNotifixationStore();

  const { RangePicker } = DatePicker;

  const mutation = useCreateMenu({
    onSuccess: () => {
      notification.addNotification({
        id: Math.random().toString(),
        message: 'Success!',
        code: 200,
        description: 'Thanks for your contribution!',
        type: 'success',
      });
      onFinish();
    },
    onError: (error) => {
      notification.addNotification({
        id: Math.random().toString(),
        message: 'Error!',
        code: error.response?.status,
        description: error.message,
        type: 'error',
      });
    },
  });

  const onFoodChange = (value: string) => {
    setFood(value);
    form.setFieldValue('food', value);
  };

  const onOk = () => {
    form.submit();
  };

  const addToMenu = () => {
    mutation.mutate({
      from: selectedDates[0],
      to: selectedDates[1],
      foodId: food,
    });
  };

  const onDateChange = (_: any, dateString: string[]) => {
    form.setFieldValue('to', dateString[1]);
    setSelectedDates(dateString);
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: string }
  ) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

  const handleRefetch = () => {
    refetch().then((res) => {
      const newAddedFood = res.data?.find((f) => !foods?.includes(f));
      if (newAddedFood) {
        onFoodChange(newAddedFood?.externalId);
      }
    });
  };

  return (
    <Modal
      open={open}
      title='Add food to menu'
      okText='Add'
      cancelText='Cancel'
      onCancel={onClose}
      onOk={onOk}
      confirmLoading={mutation.isLoading}
    >
      <Form
        onFocus={() => handleRefetch()}
        layout='vertical'
        name='foodToMenuForm'
        form={form}
        onFinish={addToMenu}
      >
        <Form.Item
          name='food'
          label='Food'
          rules={[
            {
              required: true,
              message: 'Please select the food',
            },
          ]}
        >
          <Select
            showSearch
            value={food}
            filterOption={filterOption}
            style={{ width: '80%' }}
            onChange={onFoodChange}
            options={
              foods?.map((food) => ({
                value: food.externalId,
                label: food.name,
              })) || []
            }
          />

          <Button type='link' size='large' onClick={onAddFood}>
            <PlusOutlined style={{ fontSize: 20 }} />
          </Button>
        </Form.Item>
        <Form.Item
          name='to'
          label='Available Date'
          rules={[
            {
              required: true,
              message: 'Please select the available date',
            },
          ]}
        >
          <Space direction='vertical' size={12}>
            <RangePicker onChange={onDateChange} />
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddFoodToMenu;
